import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Text,
  Flex,
  SimpleGrid,
  Spinner,
  HStack,
  Breadcrumb,
  Input,
  Image,
  VStack,
} from "@chakra-ui/react";
import { MdLocationOn, MdChevronRight, MdEdit, MdTravelExplore } from "react-icons/md";
import { Button } from "components/ui/Button";
import FormDialog from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";
import {
  useDashboardActividadesMeta,
  useDashboardActividades,
  useDeleteDashboardActividad,
  usePatchDashboardActividad,
} from "features/dashboard/actividades/hooks";
import {
  usePatchDashboardLugar,
  useSearchDashboardGooglePlaces,
  useSelectDashboardGooglePlace,
  useListDashboardGooglePhotos,
  useSelectDashboardGooglePhoto,
} from "features/dashboard/lugares/hooks";
import {
  normalizeActividadForCard,
  compareActividadesForDisplay,
} from "../Actividades/actividadesList.helpers";
import CardActs from "../Actividades/components/CardActs";

const LugarDetail = () => {
  const { id: lugarId } = useParams();
  const navigate = useNavigate();
  const metaQ = useDashboardActividadesMeta();
  const actsQ = useDashboardActividades();
  const patchLugar = usePatchDashboardLugar();
  const searchPlaces = useSearchDashboardGooglePlaces();
  const selectPlace = useSelectDashboardGooglePlace();
  const listPhotos = useListDashboardGooglePhotos();
  const selectPhoto = useSelectDashboardGooglePhoto();
  const deleteAct = useDeleteDashboardActividad();
  const patchAct = usePatchDashboardActividad();
  const editDialog = useDialogForm({ nombre: "" });
  const [photosOpen, setPhotosOpen] = useState(false);
  const [selectedPhotoName, setSelectedPhotoName] = useState("");

  const lugares = metaQ.data?.lugares ?? [];
  const lugar = lugares.find((l) => l.id === lugarId);

  const actividadesFiltradas = useMemo(() => {
    const rows = Array.isArray(actsQ.data) ? actsQ.data : [];
    return rows
      .filter((a) => a.lugarActividadId === lugarId)
      .sort(compareActividadesForDisplay);
  }, [actsQ.data, lugarId]);

  const cards = useMemo(
    () => actividadesFiltradas.map(normalizeActividadForCard),
    [actividadesFiltradas]
  );
  const photoOptions = useMemo(
    () => (Array.isArray(listPhotos.data) ? listPhotos.data.slice(0, 9) : []),
    [listPhotos.data]
  );

  const onEditOpen = () => {
    searchPlaces.reset();
    selectPlace.reset();
    listPhotos.reset();
    selectPhoto.reset();
    setPhotosOpen(false);
    setSelectedPhotoName(lugar?.googlePhotoName ?? "");
    editDialog.openWith({
      nombre: lugar?.nombre ?? "",
    });
    if (lugarId && lugar?.googlePlaceId) {
      listPhotos.mutate(lugarId);
    }
  };

  const onEditSave = async () => {
    const nombre = editDialog.form.nombre.trim();
    if (!nombre) return;
    try {
      await patchLugar.mutateAsync({ lugarId, patch: { nombre } });
      if (selectedPhotoName && selectedPhotoName !== lugar?.googlePhotoName) {
        await selectPhoto.mutateAsync({ lugarId, photoName: selectedPhotoName });
      }
      editDialog.close();
    } catch (_e) {}
  };

  const onGoogleSearch = async () => {
    const query = editDialog.form.nombre.trim();
    if (!query) return;
    try {
      await searchPlaces.mutateAsync(query);
    } catch (_e) {}
  };

  const onGoogleSelect = async (placeId) => {
    if (!lugarId || !placeId) return;
    try {
      await selectPlace.mutateAsync({ lugarId, placeId });
      searchPlaces.reset();
      listPhotos.reset();
      setPhotosOpen(true);
      setSelectedPhotoName("");
      await listPhotos.mutateAsync(lugarId);
    } catch (_e) {}
  };

  const onPhotoSelect = (photoName) => {
    if (!photoName) return;
    setSelectedPhotoName(photoName);
  };

  const onDelete = (id) => deleteAct.mutate(id);
  const onTogglePublish = (id, published) =>
    patchAct.mutate({ actividadId: id, patch: { published: !published } });

  const isLoading = metaQ.isLoading || actsQ.isLoading;
  const crumbTitle = lugar?.nombre ?? "Lugar";

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumb.Root pb="2">
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link asChild>
              <Link to="/dashboard/club/lugares">
                <HStack gap="1">
                  <MdLocationOn />
                  <Text>Lugares</Text>
                </HStack>
              </Link>
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator>
            <MdChevronRight />
          </Breadcrumb.Separator>
          <Breadcrumb.Item>
            <Breadcrumb.CurrentLink>{crumbTitle}</Breadcrumb.CurrentLink>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb.Root>

      {/* Header */}
      <Flex
        align={{ base: "flex-start", md: "center" }}
        justify="space-between"
        mb="6"
        gap="4"
        direction={{ base: "column", sm: "row" }}
      >
        <Flex
          as={lugar?.googleMapsUri ? "a" : "div"}
          href={lugar?.googleMapsUri || undefined}
          target={lugar?.googleMapsUri ? "_blank" : undefined}
          rel={lugar?.googleMapsUri ? "noreferrer" : undefined}
          align="center"
          gap="3"
          minW={0}
          flex="1"
          rounded="xl"
          textDecoration="none"
          cursor={lugar?.googleMapsUri ? "pointer" : "default"}
          _hover={lugar?.googleMapsUri ? { textDecoration: "none" } : undefined}
          _focusVisible={{
            outline: "2px solid",
            outlineColor: "dashboard.accent.border",
            outlineOffset: "3px",
          }}
        >
          {lugar?.googlePhotoUri ? (
            <Image
              src={lugar.googlePhotoUri}
              alt={lugar.nombre}
              boxSize={{ base: "64px", md: "72px" }}
              borderRadius="16px"
              objectFit="cover"
              bg="dashboard.bg.subtle"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
              flexShrink={0}
            />
          ) : (
            <Box
              boxSize={{ base: "64px", md: "72px" }}
              borderRadius="16px"
              bg="dashboard.bg.choiceSelected"
              color="dashboard.fg.muted"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <MdLocationOn size="28px" />
            </Box>
          )}
          <Box>
            <Text
              fontFamily="display"
              fontWeight="700"
              fontSize={{ base: "2xl", md: "3xl" }}
              color="dashboard.fg.default"
              lineHeight="1.1"
            >
              {crumbTitle}
            </Text>
            {lugar?.formattedAddress ? (
              <Text fontSize="sm" color="dashboard.fg.muted" mt="1" lineHeight="1.35" lineClamp={2}>
                {lugar.formattedAddress}
              </Text>
            ) : null}
            <Text fontSize="sm" color="dashboard.fg.subtle" mt="0.5">
              {cards.length} {cards.length === 1 ? "actividad" : "actividades"}
            </Text>
          </Box>
        </Flex>

        <HStack gap="2" alignSelf={{ base: "stretch", sm: "auto" }} flexWrap="wrap">
          <Button
            variant="surface"
            color="red"
            onClick={onEditOpen}
            radius="xl"
          >
            <MdEdit />
            Editar
          </Button>
        </HStack>
      </Flex>

      {/* Loading */}
      {isLoading && (
        <Flex justify="center" align="center" py="12">
          <Spinner />
        </Flex>
      )}

      {/* Not found */}
      {!metaQ.isLoading && !lugar && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="8"
        >
          <Text color="dashboard.fg.default" fontWeight="bold">
            Lugar no encontrado
          </Text>
          <Text color="dashboard.fg.muted" fontSize="sm" mt="1">
            Revisa que el periodo seleccionado sea el correcto.
          </Text>
          <Button
            variant="outline"
            color="dark"
            mt="4"
            radius="xl"
            onClick={() => navigate("/dashboard/club/lugares")}
          >
            Ver todos los lugares
          </Button>
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && lugar && cards.length === 0 && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="10"
          textAlign="center"
        >
          <Text color="dashboard.fg.muted">
            No hay actividades en este lugar.
          </Text>
        </Box>
      )}

      {/* Cards grid */}
      {!isLoading && cards.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} gap="4">
          {cards.map((item) => (
            <CardActs
              key={item.id}
              {...item}
              onDelete={onDelete}
              onTogglePublish={() => onTogglePublish(item.id, item.published)}
            />
          ))}
        </SimpleGrid>
      )}

      {/* Edit dialog */}
      <FormDialog
        open={editDialog.open}
        onOpenChange={editDialog.onOpenChange}
        title="Editar lugar"
        saving={
          patchLugar.isPending ||
          searchPlaces.isPending ||
          selectPlace.isPending ||
          listPhotos.isPending ||
          selectPhoto.isPending
        }
        onCancel={editDialog.close}
        onSave={onEditSave}
        saveLabel="Guardar"
        errorText={
          patchLugar.isError
            ? String(patchLugar.error?.message || patchLugar.error)
            : searchPlaces.isError
              ? String(searchPlaces.error?.message || searchPlaces.error)
              : selectPlace.isError
                ? String(selectPlace.error?.message || selectPlace.error)
                : listPhotos.isError
                  ? String(listPhotos.error?.message || listPhotos.error)
                  : selectPhoto.isError
                    ? String(selectPhoto.error?.message || selectPhoto.error)
                    : ""
        }
      >
        <VStack align="stretch" gap="5">
          <Box>
            <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" mb="2">
              Nombre
            </Text>
            <HStack align="stretch" gap="2">
              <Input
                placeholder="Nombre del lugar"
                bg="dashboard.bg.input"
                borderColor="dashboard.border.default"
                {...editDialog.bind("nombre")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onEditSave();
                }}
              />
              <Button
                variant="surface"
                color="info"
                radius="xl"
                flexShrink={0}
                disabled={searchPlaces.isPending}
                onClick={onGoogleSearch}
              >
                {searchPlaces.isPending ? <Spinner size="sm" /> : <MdTravelExplore />}
                Buscar
              </Button>
            </HStack>
          </Box>

          {Array.isArray(searchPlaces.data) && searchPlaces.data.length > 0 && (
            <VStack align="stretch" gap="2">
              {searchPlaces.data.map((place) => (
                <Box
                  key={place.id}
                  as="button"
                  type="button"
                  textAlign="left"
                  p="3"
                  rounded="lg"
                  borderWidth="1px"
                  borderColor="dashboard.border.muted"
                  bg="dashboard.bg.surface"
                  cursor="pointer"
                  _hover={{ bg: "dashboard.bg.subtle", borderColor: "dashboard.border.default" }}
                  disabled={selectPlace.isPending}
                  onClick={() => onGoogleSelect(place.id)}
                >
                  <Text color="dashboard.fg.default" fontWeight="700">
                    {place.nombre || "Lugar sin nombre"}
                  </Text>
                  <Text color="dashboard.fg.muted" fontSize="sm" mt="0.5">
                    {place.formattedAddress || "Sin direccion disponible"}
                  </Text>
                </Box>
              ))}
            </VStack>
          )}
          {Array.isArray(searchPlaces.data) && searchPlaces.data.length === 0 && (
            <Text color="dashboard.fg.muted" fontSize="sm">
              No se encontraron resultados.
            </Text>
          )}

          {photoOptions.length > 0 && (
            <Box>
              <Box
                as="button"
                type="button"
                alignSelf="start"
                color="dashboard.fg.muted"
                fontSize="xs"
                fontWeight="700"
                textDecoration="underline"
                textUnderlineOffset="3px"
                onClick={() => setPhotosOpen((open) => !open)}
                _hover={{ color: "dashboard.accent.fg" }}
                _focusVisible={{ outline: "2px solid", outlineColor: "dashboard.accent.border", outlineOffset: "2px" }}
              >
                {photosOpen ? "Ocultar imagenes" : `Mostrar imagenes (${photoOptions.length})`}
              </Box>

              {photosOpen && (
                <Box mt="3">
                  <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" mb="2">
                    Imagen
                  </Text>
                  <SimpleGrid columns={{ base: 2, md: 3 }} gap="2">
                    {photoOptions.map((photo) => {
                      const isSelected = photo.name === selectedPhotoName;
                      return (
                        <Box
                          key={photo.name}
                          as="button"
                          type="button"
                          rounded="lg"
                          overflow="hidden"
                          borderWidth="1px"
                          borderColor={isSelected ? "dashboard.accent.solid" : "dashboard.border.muted"}
                          outline={isSelected ? "3px solid" : "0"}
                          outlineColor="dashboard.accent.solid"
                          outlineOffset="2px"
                          boxShadow={isSelected ? "0 0 0 1px var(--chakra-colors-dashboard-bg-surface)" : "none"}
                          bg="dashboard.bg.surface"
                          cursor="pointer"
                          disabled={selectPhoto.isPending}
                          onClick={() => onPhotoSelect(photo.name)}
                        >
                          <Image
                            src={photo.photoUri}
                            alt="Imagen de Google Maps"
                            w="100%"
                            h="92px"
                            objectFit="cover"
                            bg="dashboard.bg.subtle"
                          />
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              )}
            </Box>
          )}
        </VStack>
      </FormDialog>
    </Box>
  );
};

export default LugarDetail;
